import { useState } from 'react';
import { Button, ICON_COLOR } from '@zep-us/zus';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { BoltFillIcon, BoltSmallRectangleColorMintIcon, BoltSmallRectangleColorVioletIcon } from '@/icons';
import { MobileDrawer, MobileDrawerLanguageSelector } from '@/layout/Header/MobileDrawer/MobileDrawer';
import { MyMenuDropdown } from '@/layout/Header/MyMenuDropdown';
import { LogInButton } from '@/layout/Header/UserManagementButton';
import { useUser } from '@/lib/auth';
import { calculateDaysUntilExpiry } from '@/module/plan/utils/calculateDaysUntilExpiry';
import { GnbLanguageSelector } from '@/shared/components/LanguageSelector';
import { useFreePlanCta, usePremiumAccess } from '@/shared/plan';
import { type PlanType, type UserInfo } from '@/types';
import { HomeLogo } from '../HomeLogo';
import { CreateButton } from './CreateButton';
import { type GnbButtonType } from './GnbButtonType';
import { HeaderContainer } from './HeaderContainer';
import { EnterCodeButton } from './PageNavigatorButton';
const CHECKOUT_HEADER_PATH_PREFIX = '/checkout';
const PUBLIC_PATH_PREFIX = '/public';
const getProductHeaderSource = (pathname: string) => pathname.startsWith(PUBLIC_PATH_PREFIX) ? 'public_header' : 'product_header';
export function ProductHeader() {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    user,
    isInitialized
  } = useUser();
  const isLoggedIn = isInitialized && user;
  const isFreeUser = Boolean(isLoggedIn && isLoggedIn.planType === 'FREE');
  const [isMobileProfileMenuOpen, setIsMobileProfileMenuOpen] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const isCheckoutPage = router.pathname.startsWith(CHECKOUT_HEADER_PATH_PREFIX);
  const mobileDrawerButtons: GnbButtonType[] = [{
    label: t('common.header.pricingGuide'),
    href: '/pricing',
    target: '_blank',
    clickEventname: 'gnb_pricing_click'
  }];
  const mobileDrawerFooter = <>
      <CreateButton compact hideRightSlot className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" />
      <EnterCodeButton type="normal" compact className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" textClassName="truncate" />
    </>;
  if (isCheckoutPage) {
    return <HeaderContainer variant="compact" className="h-[50px] min-h-[50px] justify-start border-b border-alternative py-[12px] tablet-sm:h-[58px] tablet-sm:min-h-[58px] tablet-sm:border-b-0">
        <div className="flex shrink-0 items-center gap-3xs">
          <HomeLogo location="header" redirectUrl="/public" showFullLogoFromTabletSm className="!h-[28px] !min-h-[28px] !w-[28px] !min-w-[28px] tablet-sm:!h-[28px] tablet-sm:!min-h-[28px] tablet-sm:!w-[128.8px] tablet-sm:!min-w-[128.8px]" />
        </div>
        <div className="ml-auto flex shrink-0 items-center">
          {isInitialized && user && <>
              <MyMenuDropdown className="flex tablet-sm:hidden" triggerSize={36} hideBadge mobileMenu showAdminMenu onOpenChange={setIsMobileProfileMenuOpen} />
              <MyMenuDropdown className="hidden tablet-sm:flex" triggerSize={32} showAdminMenu />
            </>}
        </div>
      </HeaderContainer>;
  }
  return <HeaderContainer variant="compact" className="h-[50px] min-h-[50px] justify-start border-b border-alternative py-[12px] tablet-sm:h-[58px] tablet-sm:min-h-[58px] tablet-sm:border-b-0" data-sentry-element="HeaderContainer" data-sentry-component="ProductHeader" data-sentry-source-file="ProductHeader.tsx">
      <div className="flex shrink-0 items-center gap-3xs">
        <HomeLogo location="header" redirectUrl="/public" showFullLogoFromTabletSm className="!h-[28px] !min-h-[28px] !w-[28px] !min-w-[28px] tablet-sm:!h-[28px] tablet-sm:!min-h-[28px] tablet-sm:!w-[128.8px] tablet-sm:!min-w-[128.8px]" data-sentry-element="HomeLogo" data-sentry-source-file="ProductHeader.tsx" />
      </div>

      <div className="ml-[12px] flex min-w-0 flex-1 items-center justify-end gap-2xs tablet-sm:ml-auto tablet-sm:flex-none tablet-sm:shrink-0">
        <div className="flex min-w-0 flex-1 items-center justify-end gap-xs tablet-sm:hidden">
          {isLoggedIn ? <>
              {!isMobileProfileMenuOpen && <>
                  <EnterCodeButton type="normal" compact className="min-w-[30px] max-w-full shrink overflow-hidden" textClassName="truncate" />
                  <CreateButton compact hideRightSlot className="max-w-[92px] shrink-0" />
                </>}
              <MyMenuDropdown className="flex tablet-sm:hidden" triggerSize={36} hideBadge mobileMenu showAdminMenu onOpenChange={setIsMobileProfileMenuOpen} />
            </> : isInitialized && !user && <>
                {!isMobileDrawerOpen && <>
                    <CreateButton compact hideRightSlot />
                    <LogInButton type="normal" trackingLocation={getProductHeaderSource(router.pathname)} className="h-[30px] min-h-[30px] rounded-xs px-xs text-caption-lg-semibold" />
                  </>}
                <MobileDrawer buttons={mobileDrawerButtons} bodyBottomAccessory={<MobileDrawerLanguageSelector />} footerBottomAccessory={mobileDrawerFooter} onOpenChange={setIsMobileDrawerOpen} />
              </>}
        </div>
        <div className="hidden gap-xs tablet-sm:flex tablet-sm:items-center">
          {isLoggedIn && <HeaderPlanStatusButton user={isLoggedIn} />}
          <EnterCodeButton type="normal" size="xs" data-sentry-element="EnterCodeButton" data-sentry-source-file="ProductHeader.tsx" />
          <CreateButton size="xs" hideRightSlot={isFreeUser} data-sentry-element="CreateButton" data-sentry-source-file="ProductHeader.tsx" />
          {isInitialized && user && <MyMenuDropdown className="hidden tablet-sm:flex" triggerSize={isFreeUser ? 36 : 32} showAdminMenu />}
          {isInitialized && !user && <>
              <LogInButton type="normal" size="xs" trackingLocation={getProductHeaderSource(router.pathname)} className="hidden tablet-sm:flex" />
              <GnbLanguageSelector size="xs" iconSize="xs" />
            </>}
        </div>
      </div>
    </HeaderContainer>;
}
const HeaderPlanStatusButton = ({
  user
}: {
  user: UserInfo;
}) => {
  if (user.planType === 'FREE') {
    return <FreePlanHeaderButton />;
  }
  return <PaidPlanHeaderButton user={user} data-sentry-element="PaidPlanHeaderButton" data-sentry-component="HeaderPlanStatusButton" data-sentry-source-file="ProductHeader.tsx" />;
};

/**
 * FREE 사용자의 헤더 CTA (Figma 28913:53540).
 *
 * 기존에는 「FREE 플랜 이용 중」이라고 상태만 알렸다. 시안은 **지금 할 수 있는 일**을
 * 말하게 바꿨고, 같은 FREE 라도 네 가지 상태로 갈린다.
 *
 * | 상태 | 라벨 | 동작 |
 * |---|---|---|
 * | 핸무체 이용권 보유 | 2주 PRO 체험 시작하기 | 체험 시작 다이얼로그 |
 * | 일반 이용권 보유 | 이용권 사용하기 | 사용 가능한 이용권 안내 다이얼로그 |
 * | 휴대폰 미인증(핸무체 대상) | PRO 플랜 2주 무료 체험하기 | /verification-phone |
 * | 해외 이무체 대상 | PRO 플랜 2주 무료 체험하기 | 즉시 발급 + 보상 모달 |
 * | 그 외 | 플랜 업그레이드 | /pricing 새 탭 |
 */
const FreePlanHeaderButton = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    state: headerState,
    isPending,
    execute
  } = useFreePlanCta({
    surface: 'product-header'
  });
  if (headerState.type === 'loading') {
    return <div aria-hidden className="h-[30px] w-40 rounded-full skeleton-shimmer" />;
  }
  const label = (() => {
    switch (headerState.type) {
      case 'trial-voucher':
        return t('common:plan.freeTrial.start');
      case 'usable-voucher':
        return t('common:voucher.action.activate');
      // 이무체(해외)도 같은 문구다 — 아직 이용권이 없고 "체험을 받는다"는 제안이라
      // 시안이 두 상태에 같은 라벨(13810:3829)을 쓴다. 키 이름만 인증 시절 것이다.
      case 'phone-trial':
      case 'two-weeks-trial':
        return t('common:plan.freeTrial.phoneVerification');
      case 'upgrade':
        return t('common:planUpgrade');
    }
  })();
  return <PlanCtaChip label={label} disabled={isPending} onClick={() => void execute()} data-sentry-element="PlanCtaChip" data-sentry-component="FreePlanHeaderButton" data-sentry-source-file="ProductHeader.tsx" />;
};

/**
 * 시안 공통 칩 — `Button/transparent/neutral` 에 violet 테두리와 그림자를 얹은 형태.
 * ZUS Button 에는 이 조합의 variant 가 없어 className 으로 보강한다.
 */
const PlanCtaChip = ({
  label,
  disabled,
  onClick
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) => <Button variant="transparent" intent="neutral" size="xs" rounded="full" className="cursor-pointer border border-violet-400 bg-white shadow-sm" leftSlot={<BoltSmallRectangleColorVioletIcon size="xs" />} disabled={disabled} onClick={onClick} data-sentry-element="Button" data-sentry-component="PlanCtaChip" data-sentry-source-file="ProductHeader.tsx">
    {label}
  </Button>;
const PaidPlanHeaderButton = ({
  user
}: {
  user: UserInfo;
}) => {
  const router = useRouter();
  const {
    openPlanGuide
  } = usePremiumAccess();
  const handleClick = () => {
    if (user.planType === 'PRO') {
      window.open('/pricing', '_blank', 'noopener,noreferrer');
      return;
    }
    void openPlanGuide({
      highlightPlan: 'PRO',
      from: getProductHeaderSource(router.pathname)
    });
  };
  return <Button variant="transparent" intent="neutral" size="xs" className="cursor-pointer" leftSlot={<HeaderPlanIcon planType={user.planType} />} onClick={handleClick} data-sentry-element="Button" data-sentry-component="PaidPlanHeaderButton" data-sentry-source-file="ProductHeader.tsx">
      <HeaderPlanStatusText planType={user.planType} endDate={user.planDuration?.endDate ?? ''} data-sentry-element="HeaderPlanStatusText" data-sentry-source-file="ProductHeader.tsx" />
    </Button>;
};
const HeaderPlanIcon = ({
  planType
}: {
  planType: PlanType;
}) => {
  if (planType === 'PRO') {
    return <BoltSmallRectangleColorVioletIcon size="xs" />;
  }
  if (planType === 'BASIC') {
    return <BoltSmallRectangleColorMintIcon size="xs" />;
  }
  return <span className="flex size-[12px] shrink-0 items-center justify-center rounded-[2px] bg-gray-300" data-sentry-component="HeaderPlanIcon" data-sentry-source-file="ProductHeader.tsx">
      <BoltFillIcon width={9} height={9} fill={ICON_COLOR.inverse} aria-hidden="true" data-sentry-element="BoltFillIcon" data-sentry-source-file="ProductHeader.tsx" />
    </span>;
};
const HeaderPlanStatusText = ({
  planType,
  endDate
}: {
  planType: PlanType;
  endDate: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  if (planType === 'FREE') {
    return <span>{t('common:plan.usingFree')}</span>;
  }
  const daysRemaining = calculateDaysUntilExpiry(endDate);
  const daysColorClass = planType === 'BASIC' ? 'text-semi-strong' : 'text-strong';
  if (daysRemaining < 0) {
    return <span>{t('common:plan.usingPlan', {
        planType
      })}</span>;
  }
  return <span data-sentry-component="HeaderPlanStatusText" data-sentry-source-file="ProductHeader.tsx">
      <Trans t={t} i18nKey="common:plan.daysLeft" values={{
      planType,
      days: daysRemaining
    }} components={{
      Days: <span className={daysColorClass} />
    }} data-sentry-element="Trans" data-sentry-source-file="ProductHeader.tsx" />
    </span>;
};